import React, { useEffect, useMemo, useRef } from "react";
import donViHanhChinh from "../data/donViHanhChinh.json";

/**
 * Chọn địa chỉ theo mô hình 2 CẤP: tỉnh/thành -> phường/xã, kèm ô địa chỉ chi tiết.
 *
 * Bỏ cấp huyện theo Luật Tổ chức chính quyền địa phương 2025 (hiệu lực 01/07/2025) —
 * đúng với cách VNeID hiển thị địa chỉ sau sáp nhập.
 *
 * Dữ liệu nhúng thẳng trong bundle (src/data/donViHanhChinh.json, ~27KB sau gzip)
 * thay vì gọi API: chọn xã hiện ra tức thì, không thêm request. Khi nhà nước điều
 * chỉnh địa giới thì sinh lại file.
 *
 * Cấu trúc file rút gọn khoá cho nhẹ: i = mã số, n = tên, w = danh sách phường/xã.
 * Mã số giữ nguyên theo chuẩn Tổng cục Thống kê.
 *
 * Dùng với formik: truyền `formik` và tên 3 cột; component tự đọc/ghi giá trị,
 * kể cả khi mở form sửa (dữ liệu về sau lần render đầu).
 */
export default function ChonDiaChi({
    formik,
    nameGop = "address_tc",
    nameTinh = "tinh_id",
    namePhuongXa = "phuongxa_id",
    nameChiTiet = "address_tc_chitiet",
    label = "Địa chỉ thường trú",
    batBuoc = true,
    disabled,
}) {
    const tinhId = formik.values[nameTinh] || "";
    const phuongXaId = formik.values[namePhuongXa] || "";
    const chiTiet = formik.values[nameChiTiet] || "";
    const chuoiGop = formik.values[nameGop] || "";

    const tinhDangChon = useMemo(
        () => donViHanhChinh.find((t) => String(t.i) === String(tinhId)),
        [tinhId]
    );
    const dsPhuongXa = tinhDangChon ? tinhDangChon.w : [];

    // Bản ghi cũ chỉ có address_tc dạng chuỗi, chưa tách phần. Thử dò ngược tên
    // tỉnh/xã trong chuỗi đó để chọn sẵn ô select, phần còn lại đưa vào ô chi tiết.
    // Chỉ chạy MỘT lần khi dữ liệu về, không đụng tới bản ghi đã tách sẵn.
    const daDoNguoc = useRef(false);
    useEffect(() => {
        if (daDoNguoc.current) return;
        if (tinhId || !chuoiGop) return;

        daDoNguoc.current = true;
        const chuoi = chuoiGop.toLowerCase();
        const tinh = donViHanhChinh.find((t) => chuoi.includes(t.n.toLowerCase()));
        if (!tinh) return;

        const xa = tinh.w.find((w) => chuoi.includes(w.n.toLowerCase()));
        // Cắt bỏ phần tên tỉnh/xã khỏi chuỗi để lấy địa chỉ chi tiết còn lại.
        let conLai = chuoiGop;
        [xa?.n, tinh.n].forEach((ten) => {
            if (ten) conLai = conLai.replace(new RegExp(ten, "i"), "");
        });
        conLai = conLai.replace(/[,\s]+$/g, "").replace(/^[,\s]+/g, "").replace(/,\s*,/g, ",").trim();

        formik.setFieldValue(nameTinh, tinh.i);
        if (xa) formik.setFieldValue(namePhuongXa, xa.i);
        if (conLai) formik.setFieldValue(nameChiTiet, conLai);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chuoiGop, tinhId]);

    /**
     * Ghép chuỗi địa chỉ đầy đủ và ghi vào formik.
     * Chuỗi gộp lưu vào address_tc — mẫu hợp đồng đọc placeholder {dcthuongtru} từ đó.
     */
    const capNhat = (thayDoi) => {
        const next = { tinh_id: tinhId, phuongxa_id: phuongXaId, chi_tiet: chiTiet, ...thayDoi };

        const tinh = donViHanhChinh.find((t) => String(t.i) === String(next.tinh_id));
        const tenTinh = tinh ? tinh.n : "";
        // Đổi tỉnh thì xã cũ không còn thuộc tỉnh mới => tên xã lấy rỗng.
        const tenXa =
            thayDoi.tinh_id !== undefined
                ? ""
                : (dsPhuongXa.find((x) => String(x.i) === String(next.phuongxa_id))?.n || "");

        const dayDu = [next.chi_tiet, tenXa, tenTinh]
            .map((p) => (p || "").trim())
            .filter((p) => p !== "")
            .join(", ");

        formik.setFieldValue(nameTinh, next.tinh_id);
        formik.setFieldValue(namePhuongXa, next.phuongxa_id);
        formik.setFieldValue(nameChiTiet, next.chi_tiet);
        formik.setFieldValue(nameGop, dayDu);
    };

    const sao = batBuoc ? <span className="text-danger">*</span> : null;

    return (
        <>
            <div className="col-12 col-sm-4 form-group">
                <label>{label} — Tỉnh/Thành phố {sao}</label>
                <select
                    className="form-select height32"
                    value={tinhId}
                    disabled={disabled}
                    onChange={(e) => capNhat({ tinh_id: e.target.value, phuongxa_id: "" })}
                >
                    <option value="">Chọn tỉnh/thành phố</option>
                    {donViHanhChinh.map((t) => (
                        <option key={t.i} value={t.i}>{t.n}</option>
                    ))}
                </select>
            </div>

            <div className="col-12 col-sm-4 form-group">
                <label>Phường/Xã {sao}</label>
                <select
                    className="form-select height32"
                    value={phuongXaId}
                    disabled={disabled || !tinhId}
                    onChange={(e) => capNhat({ phuongxa_id: e.target.value })}
                >
                    <option value="">{!tinhId ? "Chọn tỉnh/thành trước" : "Chọn phường/xã"}</option>
                    {dsPhuongXa.map((x) => (
                        <option key={x.i} value={x.i}>{x.n}</option>
                    ))}
                </select>
            </div>

            <div className="col-12 col-sm-4 form-group">
                <label>Số nhà, thôn/xóm, tổ {sao}</label>
                <input
                    type="text"
                    className="form-control height32"
                    placeholder="VD: Số 12, ngõ 5, thôn Đông"
                    value={chiTiet}
                    disabled={disabled}
                    onChange={(e) => capNhat({ chi_tiet: e.target.value })}
                    autoComplete="off"
                />
            </div>

            {chuoiGop && (
                <div className="col-12 mb-2">
                    <small className="text-muted">Địa chỉ đầy đủ: </small>
                    <strong style={{ fontSize: 14 }}>{chuoiGop}</strong>
                </div>
            )}
        </>
    );
}
